import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslations } from '../utils/translations';
import API_URL from '../config/api';
import emailjs from '@emailjs/browser';
import './Contact.css';

export default function Contact() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Send email via EmailJS
      let emailSent = false;
      try {
        await emailjs.send(
          'service_r8rvio8',
          'template_akg3o96',
          {
            from_name: form.name,
            from_email: form.email,
            message: form.message,
            to_email: 'contact@sefarium.com',
          }
        );
        emailSent = true;
        console.log('Email sent successfully via EmailJS');
      } catch (emailError) {
        console.error('EmailJS error:', emailError);
        // Don't fail the form if email fails - still try to save to database
      }

      // Save to database (backend)
      try {
        const res = await fetch(`${API_URL}/api/inquiry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
          mode: 'cors'
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: 'Server error' }));
          throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        if (data.success === true) {
          setSubmitted(true);
          setForm({ name: '', email: '', message: '' });
        } else {
          setError(data.error || data.message || t.contact.error);
        }
      } catch (dbError) {
        console.error('Database error:', dbError);
        // If email was sent, still show success
        if (emailSent) {
          setSubmitted(true);
          setForm({ name: '', email: '', message: '' });
        } else {
          setError(dbError.message || t.contact.error || 'Failed to send message. Please try again or contact us directly.');
        }
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || t.contact.error || 'Failed to send message. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="contact">
        <div className="contact-wrapper">
          <div className="contact-info">
            <h2>{t.contact.title}</h2>
            <p className="contact-subtitle">{t.contact.subtitle}</p>
            
            <div className="contact-info-list">
              <div className="contact-info-item">
                <h3>Contact</h3>
                <a href="mailto:contact@sefarium.com">contact@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Partnerships</h3>
                <a href="mailto:partnerships@sefarium.com">partnerships@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Business Travel</h3>
                <a href="mailto:corporate@sefarium.com">corporate@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Phone</h3>
                <a href="tel:+48730759753">+48 730 759 753</a>
                <a href="tel:+48698096309">+48 698 096 309</a>
              </div>
            </div>
          </div>
          
          <div className="contact-form-container">
            <div className="contact-success">
              <div className="success-icon">✓</div>
              <h2>{t.contact.success}</h2>
              <p>{t.contact.successMessage}</p>
              <button onClick={() => setSubmitted(false)} className="contact-button">
                {t.contact.sendAnother}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="contact" className="contact">
        <div className="contact-wrapper">
          <div className="contact-info">
            <h2>{t.contact.title}</h2>
            <p className="contact-subtitle">{t.contact.subtitle}</p>
            
            <div className="contact-info-list">
              <div className="contact-info-item">
                <h3>Contact</h3>
                <a href="mailto:contact@sefarium.com">contact@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Partnerships</h3>
                <a href="mailto:partnerships@sefarium.com">partnerships@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Business Travel</h3>
                <a href="mailto:corporate@sefarium.com">corporate@sefarium.com</a>
              </div>
              
              <div className="contact-info-item">
                <h3>Phone</h3>
                <a href="tel:+48730759753">+48 730 759 753</a>
                <a href="tel:+48698096309">+48 698 096 309</a>
              </div>
            </div>
          </div>
          
          <div className="contact-form-container">
            <form onSubmit={handleSubmit} className="contact-form">
              {error && <div className="contact-error">{error}</div>}
              
              <div className="contact-input-group">
                <label>{t.contact.yourName}</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t.contact.namePlaceholder}
                  required
                />
              </div>
              
              <div className="contact-input-group">
                <label>{t.contact.yourEmail}</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t.contact.emailPlaceholder}
                  required
                />
              </div>
              
              <div className="contact-input-group">
                <label>{t.contact.yourMessage}</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t.contact.messagePlaceholder}
                  rows="5"
                  required
                />
              </div>
              
              <button type="submit" className="contact-button" disabled={loading}>
                {loading ? t.contact.sending : t.contact.send}
              </button>
            </form>
          </div>
        </div>
      </section>
      
      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/48730759753" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </>
  );
}




